/**
 * How It Works Section
 * Custom CSS/SVG flow visualization with animated connections
 */

import { motion } from 'framer-motion';
import { Code, Brain, Check } from 'lucide-react';

const steps = [
  {
    number: 1,
    icon: <Code className="w-12 h-12 text-primary" />,
    title: 'Send Your Prompt',
    description:
      'Use our OpenAI SDK-compatible API. Drop-in replacement for your existing code.',
    content: (
      <div className="bg-muted/50 p-4 rounded-lg text-left text-sm font-mono">
        <pre className="text-xs md:text-sm overflow-x-auto">
          {`import { Pathfinder } from '@pathfinder/sdk'

const client = new Pathfinder({
  apiKey: process.env.PATHFINDER_KEY
})

const response = await client.chat
  .completions.create({
    messages: [{
      role: 'user',
      content: 'Your prompt'
    }],
    // Pathfinder automatically selects
    // the best model
  })`}
        </pre>
      </div>
    ),
  },
  {
    number: 2,
    icon: <Brain className="w-12 h-12 text-accent" />,
    title: 'Intelligent Routing',
    description: "ML-powered engine analyzes your prompt's features:",
    content: (
      <ul className="text-left space-y-2 mb-6">
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-primary" />
          <span>Complexity score</span>
        </li>
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-primary" />
          <span>Task type (code, creative, analysis)</span>
        </li>
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-primary" />
          <span>Context length</span>
        </li>
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-primary" />
          <span>Performance requirements</span>
        </li>
      </ul>
    ),
    footer: (
      <p className="text-sm text-muted-foreground">
        Then selects the optimal model in &lt;50ms.
      </p>
    ),
  },
];

const models = [
  {
    id: 'openai',
    name: 'OpenAI',
    model: 'GPT-5',
    icon: (
      <svg className="w-10 h-10" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path fill="white" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
      </svg>
    ),
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    model: 'Claude 4.5',
    icon: (
      <svg className="w-10 h-10" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path fill="#ea7317" d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"/>
      </svg>
    ),
  },
  {
    id: 'gemini',
    name: 'Google',
    model: 'Gemini 2.5',
    icon: (
      <svg className="w-10 h-10" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path fill="#3b82f6" d="M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81"/>
      </svg>
    ),
  },
];

export function HowItWorksSection() {
  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">How It Works</h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Three simple steps to intelligent AI routing
          </p>
        </motion.div>

        {/* Flow Container - Horizontal Layout */}
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-3 relative">
          {/* Step 1 - Send Your Prompt */}
          <motion.div
            className="md:flex-shrink-0"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ width: '400px' }}
          >
            <div className="bg-card border border-border rounded-xl p-6 text-center h-full flex flex-col justify-between">
              <div>
                <div className="mb-4 flex justify-center">{steps[0].icon}</div>
                <h3 className="text-lg md:text-xl font-semibold mb-3">
                  {steps[0].title}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-4">
                  {steps[0].description}
                </p>
              </div>
              {steps[0].content}
            </div>
          </motion.div>

          {/* Connecting line between cards 1 and 2 */}
          <div className="hidden md:flex items-center flex-shrink-0 relative" style={{ width: '60px' }}>
            <div className="h-0.5 w-full bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
          </div>

          {/* Step 2 - Intelligent Routing (smaller) */}
          <motion.div
            className="md:flex-shrink-0"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ width: '340px' }}
          >
            <div className="bg-card border border-border rounded-xl p-3 text-center flex flex-col justify-center">
              <div className="mb-2 flex justify-center">{steps[1].icon}</div>
              <h3 className="text-sm font-semibold mb-1">
                {steps[1].title}
              </h3>
              <p className="text-xs text-muted-foreground mb-2">
                {steps[1].description}
              </p>
              <div className="text-xs space-y-0.5">
                {steps[1].content}
                {steps[1].footer}
              </div>
            </div>
          </motion.div>

          {/* Connecting line between card 2 and providers */}
          <div className="hidden md:flex items-center flex-shrink-0 relative" style={{ width: '60px' }}>
            <div className="h-0.5 w-full bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
          </div>

          {/* Three Models - Vertical Stack with spacing */}
          <motion.div
            className="flex flex-col gap-6"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ justifyContent: 'center' }}
          >
            {models.map((model) => (
              <div
                key={model.id}
                className="bg-card border border-border rounded-xl p-2 text-center hover:shadow-lg transition-shadow"
                style={{ width: '140px' }}
              >
                <div className="mb-1 flex justify-center">{model.icon}</div>
                <h4 className="font-semibold text-xs">{model.name}</h4>
                <p className="text-xs text-muted-foreground">{model.model}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes flow {
          0% {
            stroke-dashoffset: 20;
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
          100% {
            stroke-dashoffset: 0;
            opacity: 0.6;
          }
        }
      `}</style>
    </section>
  );
}
